<template>
  <div>
    <table class="tw-bg-white tw-p-12 tw-w-full">
      <thead>
        <tr class="tw-bg-[#00345d] tw-text-white">
          <th class="tw-p-2 tw-text-left">
            <button @click="toggleEdit" class="tw-bg-transparent tw-border-none tw-cursor-pointer">
              <i class="fas fa-pencil-alt tw-text-white"></i>
            </button>
          </th>
          <th class="tw-p-2 tw-text-left">Principle</th>
          <th class="tw-p-2 tw-text-left">State of Portfolio</th>
          <th class="tw-p-2 tw-text-left">Recommended State</th>
          <th class="tw-p-2 tw-text-left table-status">Status</th>
          <th class="tw-p-2 tw-text-left table-reason">Reason</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td colspan="6" class="tw-text-center tw-p-4">Loading principles...</td>
        </tr>
        <tr v-else-if="error">
          <td colspan="6" class="tw-text-center tw-p-4 tw-text-red-500">{{ error }}</td>
        </tr>
        <template v-else>
          <tr v-for="(principle, index) in principles" 
              :key="index" 
              :class="getStatusClass(principle.status)">
            <td class="tw-p-2">
              <button v-if="isEditing" @click="removePrinciple(index)" class="tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer">x</button>
            </td>
            <td class="tw-p-2">
              <textarea 
                v-model="principle.principle" 
                placeholder="add text here" 
                class="tw-w-full tw-bg-transparent tw-border-none tw-focus:outline-none tw-resize-none" 
                @input="autoGrow($event.target)"
              ></textarea>
            </td>
            <td class="tw-p-2">
              <textarea 
                v-model="principle.current_state" 
                placeholder="add text here" 
                class="tw-w-full tw-bg-transparent tw-border-none tw-focus:outline-none tw-resize-none"
                @input="autoGrow($event.target)"
              ></textarea>
            </td>
            <td class="tw-p-2">
              <textarea v-model="principle.recommended_state" placeholder="add text here" class="tw-w-full tw-bg-transparent tw-border-none tw-focus:outline-none tw-resize-none" rows="3"></textarea>
            </td>
            <td class="tw-p-2 table-status">
              <textarea v-model="principle.status" placeholder="" class="tw-w-full tw-bg-transparent tw-border-none tw-focus:outline-none tw-resize-none" rows="3"></textarea>
            </td>
            <td class="tw-p-2 table-reason">
              <textarea v-model="principle.reason" placeholder="add text here" class="tw-w-full tw-bg-transparent tw-border-none tw-focus:outline-none tw-resize-none" rows="4"></textarea>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';

export default {
  name: 'EditablePrinciplesTable',
  data() {
    return {
      isEditing: false,
      principles: [],
      loading: false,
      error: null
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
  },
  mounted() {
    this.fetchPrinciples();
    this.$nextTick(() => {
      document.querySelectorAll('textarea').forEach(el => this.autoGrow(el));
    });
  },
  updated() {
    this.$nextTick(() => {
      document.querySelectorAll('textarea').forEach(el => this.autoGrow(el));
    });
  },
  methods: {
    async fetchPrinciples() {
      if (!this.checkupStore.portfolio && !this.checkupStore.optimizedPortfolio) return;
      
      this.loading = true;
      this.error = null;
      
      try {
        const params = new URLSearchParams();
        if (this.checkupStore.portfolio) {
          params.append('portfolio_id', this.checkupStore.portfolio.external_id);
        }
        if (this.checkupStore.optimizedPortfolio) {
          params.append('proposed_id', this.checkupStore.optimizedPortfolio.external_id);
        }
        
        const response = await fetch(`/api/v1/checkup/get-principles/?${params.toString()}`);
        const data = await response.json();
        console.log('Principles Response:', data);
        
        if (response.ok) {
          this.principles = data.principles;
          // Add slight delay to ensure content is rendered before sizing
          this.$nextTick(() => {
            setTimeout(() => {
              this.initializeTextareas();
            }, 0);
          });
          this.checkupStore.updateArea('Asset Location', {...this.checkupStore.areasByName['Asset Location'], 'principles': this.principles})
        } else {
          this.error = data.error || 'Failed to load principles';
        }
      } catch (err) {
        console.error('Error fetching principles:', err);
        this.error = 'Failed to load principles';
      } finally {
        this.loading = false;
      }
    },
    toggleEdit() {
      this.isEditing = !this.isEditing;
    },
    addPrinciple() {
      this.principles.push({
        principle: '',
        current_state: '',
        recommended_state: '',
        status: '',
        reason: ''
      });
      this.$nextTick(() => {
        this.initializeTextareas();
      });
    },
    removePrinciple(index) {
      this.principles.splice(index, 1);
    },
    getStatusClass(status) {
      if (status?.toLowerCase() === 'on target') {
        return 'status-check-plus';
      } else if (status?.toLowerCase() === 'needs review') {
        return 'status-x';
      }
      return '';
    },
    autoGrow(element) {
      if (!element) return;
      // Set a minimum height before calculating
      element.style.height = '40px';
      element.style.height = (element.scrollHeight) + 'px';
    },
    initializeTextareas() {
      document.querySelectorAll('textarea').forEach(el => {
        this.autoGrow(el);
        // Add resize observer to handle dynamic content changes
        const resizeObserver = new ResizeObserver(() => {
          this.autoGrow(el);
        });
        resizeObserver.observe(el);
      });
    }
  },
  watch: {
    'checkupStore.portfolio': {
      handler: 'fetchPrinciples'
    },
    'checkupStore.optimizedPortfolio': {
      handler: 'fetchPrinciples'
    },
    principles: {
      handler() {
        this.$nextTick(() => {
          this.initializeTextareas();
        });
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.table-status {
  width: 80px;
}

.table-reason {
  width: 300px;
}

.status-check-plus {
  background-color: #C8E6C9;
}

.status-x {
  background-color: #FCE4A7;
}

table th {
  color: white;
}

textarea:focus, input:focus {
  outline: 1px solid #00345d;
}

textarea {
  overflow-y: hidden;
  min-height: 40px;
  line-height: 1.5;
  padding: 8px 0;
}

button {
  cursor: pointer;
}
</style>
